import { GetProposalQuery } from '@/gql/graphql';
import {
  QueryClient,
  QueryKey,
  UseMutationOptions,
} from '@tanstack/react-query';

type OptimisticContext = { previousProposal?: GetProposalQuery };

/**
 * Build the shared `onMutate` / `onError` handlers for an optimistic proposal
 * mutation: `apply` is run against the cached proposal in `onMutate` so the UI
 * updates immediately, and the previous proposal is restored on error. The base
 * mutation hook is expected to invalidate the proposal on success, reconciling
 * the optimistic write with server truth.
 *
 * Callers may pass their own `onMutate` / `onError` via `options`; they are
 * chained after the optimistic write / rollback.
 */
export const createOptimisticProposalMutationOptions = <
  TData,
  TVariables,
>(args: {
  queryClient: QueryClient;
  queryKey: QueryKey;
  apply: (data: GetProposalQuery, variables: TVariables) => GetProposalQuery;
  options?: UseMutationOptions<TData, unknown, TVariables>;
}): Pick<
  UseMutationOptions<TData, unknown, TVariables>,
  'onMutate' | 'onError'
> => {
  const { queryClient, queryKey, apply, options } = args;

  return {
    onMutate: async (variables) => {
      // Cancel in-flight refetches so they don't clobber the optimistic write.
      await queryClient.cancelQueries({ queryKey });

      const previousProposal =
        queryClient.getQueryData<GetProposalQuery>(queryKey);

      queryClient.setQueryData<GetProposalQuery>(queryKey, (old) =>
        old ? apply(old, variables) : old
      );

      await options?.onMutate?.(variables);

      return { previousProposal } satisfies OptimisticContext;
    },
    onError: (error, variables, context) => {
      const ctx = context as OptimisticContext | undefined;
      if (ctx?.previousProposal) {
        queryClient.setQueryData(queryKey, ctx.previousProposal);
      }
      options?.onError?.(error, variables, context);
    },
  };
};
